import React, { useState } from 'react';
import { AlertTriangle, Trash2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import Modal from '../design-system/Modal';
import Button from '../design-system/Button';
import { Input } from '../design-system';

export default function DeleteAccountModal({ onClose, onDeleted }) {
  const [step, setStep] = useState(1);
  const [confirmText, setConfirmText] = useState('');
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const CONFIRM_WORD = 'SUPPRIMER';
  const canConfirm = checked && confirmText === CONFIRM_WORD;

  async function handleDelete() {
    if (!canConfirm) return;
    setLoading(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Session expirée, reconnecte-toi.');
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const res = await fetch(`${supabaseUrl}/functions/v1/delete-account`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Erreur lors de la suppression.');
      await supabase.auth.signOut();
      onDeleted?.();
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={<span className="flex items-center gap-2"><Trash2 size={16} className="text-rose-400" /> Supprimer le compte</span>}
      size="sm"
    >
      {step === 1 && (
        <div className="space-y-4">
          <div className="flex gap-3 bg-rose-500/10 border border-rose-500/20 p-4">
            <AlertTriangle size={18} className="text-rose-400 shrink-0 mt-0.5" />
            <div className="text-sm text-zinc-300 leading-relaxed">
              <div className="font-bold text-rose-400 mb-2">Action irréversible</div>
              La suppression de ton compte effacera définitivement :
            </div>
          </div>
          <ul className="text-sm text-zinc-400 space-y-1.5 pl-2">
            {['Ton compte et tes identifiants','Toutes tes parties importées','Toutes tes sessions et annotations','Tout ton historique LP et snapshots de rang','Tous tes paramètres et objectifs'].map((item, i) => (
              <li key={i} className="flex items-center gap-2"><div className="w-1 h-1 bg-rose-400 rounded-full shrink-0" />{item}</li>
            ))}
          </ul>
          <p className="text-xs text-zinc-500">Cette action est immédiate et impossible à annuler.</p>
          <div className="flex gap-3 pt-2">
            <Button variant="secondary" className="flex-1" onClick={onClose}>Annuler</Button>
            <Button variant="danger" className="flex-1" onClick={() => setStep(2)}>Continuer →</Button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-5">
          <p className="text-sm text-zinc-400">
            Pour confirmer, coche la case et tape <span className="font-mono font-bold text-rose-400">{CONFIRM_WORD}</span> ci-dessous.
          </p>
          <label className="flex items-start gap-3 cursor-pointer group">
            <input type="checkbox" checked={checked} onChange={e => setChecked(e.target.checked)} className="mt-0.5 accent-rose-500" />
            <span className="text-sm text-zinc-400 group-hover:text-zinc-300 transition leading-relaxed">
              Je comprends que cette action est <strong className="text-zinc-200">définitive et irréversible</strong> et que toutes mes données seront supprimées.
            </span>
          </label>
          <Input
            label={`Tape « ${CONFIRM_WORD} » pour confirmer`}
            value={confirmText}
            onChange={e => setConfirmText(e.target.value)}
            placeholder={CONFIRM_WORD}
            autoComplete="off"
            spellCheck={false}
          />
          {error && <div className="text-xs text-rose-400 bg-rose-500/10 border border-rose-500/20 p-3">{error}</div>}
          <div className="flex gap-3">
            <Button variant="secondary" className="flex-1" onClick={() => { setStep(1); setConfirmText(''); setChecked(false); }} disabled={loading}>← Retour</Button>
            <Button variant="danger" className="flex-1" onClick={handleDelete} disabled={!canConfirm || loading} loading={loading}>
              {loading ? 'Suppression...' : 'Supprimer définitivement'}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
